// src/encounter/ap-add-line.tsx
//
// PROD-3041 · frame v2 ONLY (the A&P inside the note). The ONE "Add diagnosis" / "Add order" line at the
// top of the Diagnoses and Orders blocks, replacing the legacy A&P search rail inside the note (live dev
// review, 6 Oct, F2/F3: a blue "Dx" bar, text tabs, a "Global" list, match scores as bars, two search
// entry points). Built to design-desk ruling D-75 (Diagnoses) and its Orders amendment, 6 Oct evening.
//
// COMPOSED FROM SIGNAL DS: the line is a DS `TextField` (no icon); the dropdown is the DS `CommandMenu`
// (the note's own "." / "/" menu), driven from the line through its `handleKey` handle, with
// `aria-activedescendant` naming the active row. Each row: its KIND, then the name, then the code and the
// row's real source on the second line (text-secondary, 5.53:1). No score, no percentage, no bar.
//
// DS GAPS, worked around and recorded (graduation asks for `CommandMenu`):
//   · no section headings: each row says its group instead (kind cell + "· your favourites" /
//     "· ICD-10 catalogue" / … on its second line), and rows run in group order;
//   · `CommandKind` is a closed union (Phrase · Template · Diagnosis · Lab order · Referral): the other
//     order kinds and "Order set" are passed as their own words (`menuKind`). The kind cell renders the
//     word; `lockReason` reads any non-"Diagnosis" kind as an order, which is right for every one of them;
//   · its own empty text is "No match.": the states below ("Searching…", "No diagnoses match",
//     "Couldn't search." + Retry) are drawn by the line, and the menu opens only when it has rows;
//   · its code column is text-muted (3.30:1, under 4.5:1), so the code rides the second line instead.
//
// THE DATA AND THE SAVE ARE UNCHANGED: every row comes from the hooks the rail used
// (`useApCategorySearch`: provider favourites + the master catalogue per category; the order-set list;
// the article search), and every pick goes back through the A&P view's own handlers: a diagnosis is
// added by `onAddDx`, an order opens TODAY's order dialog (interaction check, "Acknowledge & Proceed",
// role gates; accepting pends it, never sends it), an order set runs today's apply.
//
// NOT HERE: Scrips Intelligence diagnosis suggestions. They are in the right Intelligence rail
// (intelligence-dx-suggestions.tsx); lexical search matches are search, never suggestions.
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import {
  Button,
  CommandMenu,
  TextField,
  type ApSearchCategory,
  type ApSearchItem,
  type AuthorRole,
  type CommandKind,
  type CommandMenuHandle,
  type CommandMenuItem,
  type OrderType as DsOrderType,
} from '@scripsteam/scrips-signal-ds';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { DX_SLASH_KINDS, parseSlashQuery } from './lib/slash-trigger';
import { useAiDiagnosisSearch } from './services/use-ai-diagnosis-search';
import { useApCategorySearch } from './services/use-ap-category-search';
import { AddEntryButton } from './add-entry';
import { useArticleSearch } from './services/use-article-search';
import { useApplyOrderSets } from './order-sets/order-set-apply-service';
import { orderSetMetaLine, orderSetsForRail } from './order-sets/order-sets-rail-model';
import type { OrderSetApplySummary } from '@/types/order-set-apply-api';
import type { ApBundle } from './services/use-bundles';
import type { EducationArticle } from './lib/to-education-article';
import { liveSections, withoutCodes, type AddLineSection } from './lib/add-line-sections';
import { SPECIALITY_SOURCE, specialityRowHint } from './lib/referral-specialities';
import { ADD_LINE_ERROR, MIN_CATALOGUE_CHARS, ORDER_SEARCH, announcePick, failureText, flat, hintOf, lineStatus, norm, row, useRetryFailed, useSettled, type Row } from './lib/add-line-helpers';

/** How long the typed text settles before the catalogue searches run (one request per pause, not per key). */
export const ADD_LINE_DEBOUNCE_MS = 250;
/** The article search keeps Flutter's own debounce (SimpleSearchWidget). */
const ARTICLE_DEBOUNCE_MS = 600;

export const ADD_LINE_SEARCHING = 'Searching…';
export { ADD_LINE_ERROR };
export const ADD_LINE_RETRY = 'Retry';
export const DIAGNOSES_NO_MATCH = 'No diagnoses match';
/** D-75 (4): focus with no query shows your favourites, or this line when you have none. */
export const NO_FAVOURITES_YET = 'No favourites yet';
/** PROD-3423 (`dxSearchFirst`): the Diagnoses line open with no favourites says how to search (#915 review). */
export const DX_TYPE_TO_SEARCH = 'Type to search ICD-10';
export const ORDERS_NO_MATCH = 'No orders match';

/** The DS kind cell takes the row's own word (see the file header: `CommandKind` is a closed union). */
const menuKind = (word: string): CommandKind => word as CommandKind;

/** One row of the dropdown: its kind word, the name, and the second line (code · source). */
export interface AddLineEntry<T> {
  key: string;
  kind: string;
  term: string;
  /** The second line: the code first, then the row's real source. */
  hint?: string;
  /** Already on this visit: listed, not addable (the DS lock reads "Already in the note"). */
  added?: boolean;
  value: T;
}

/** What the line says under the field while it has text. */
export type AddLineStatus = 'idle' | 'searching' | 'empty' | 'error';

// ── the presentational line ────────────────────────────────────────────────────────────────────────

export interface ApAddLineProps<T> {
  /** The line's name and placeholder: "Add diagnosis" / "Add order". */
  label: string;
  /** The input's DOM id (the held-order banner focuses it). */
  inputId?: string;
  query: string;
  onQuery: (next: string) => void;
  /** Every result, in group order (`liveSections`); the DS menu holds them in a fixed-height list that scrolls (PROD-3082). */
  entries: AddLineEntry<T>[];
  status: AddLineStatus;
  /** "No diagnoses match" / "No orders match" — said only after an empty response. */
  emptyText: string;
  /** The failure line; default "Couldn't search." (Orders names the sources that failed while others answered). */
  errorText?: string;
  onRetry: () => void;
  /**
   * Handle a pick. A pick that runs on after this call (an order set's apply) returns its promise: the live region
   * says what is happening ("Applying the order set …") until it settles, then clears, because the outcome is
   * announced once by its own receipt or failure notice (PROD-3147).
   */
  onPick: (value: T) => void | PromiseLike<unknown>;
  /**
   * What the live region says after a pick (Codex P2, 6 Oct: an order pick only OPENS its form, so it is
   * never announced as added). Default "Added <name>".
   */
  announceFor?: (value: T, term: string) => string;
  /** The signed-in author (entry is gated upstream; this only feeds the DS lock rule). */
  role?: AuthorRole;
  /** D-75 (4): told when the field opens or closes, so the line can offer your favourites on focus, before any text. */
  onFieldOpenChange?: (open: boolean) => void;
  /**
   * Tab or Shift+Tab with the list open closes the list first: focus and text stay, and the live region says so. The
   * next Tab or Shift+Tab moves on as usual, so focus is never trapped. Default: both move on at once (and close the list).
   */
  tabClosesList?: boolean;
  /**
   * Enter does nothing while the line says "Searching…" (`status` 'searching'): the rows on screen may still be the
   * previous text's answer, so Enter waits for this text's rows. A click still picks the row it lands on.
   */
  holdEnterWhileSearching?: boolean;
}

/** What the live region says when Tab closes the list (`tabClosesList`). */
export const ADD_LINE_TAB_CLOSED = 'List closed. Press Tab again to move on, or ↓ to open it.';

/**
 * The line. Focus stays in the field throughout: the menu takes ↑ ↓ Enter Esc from it, Tab moves on
 * (and closes the menu; with `tabClosesList`, the first Tab only closes it), Esc closes the menu and keeps
 * the text. Enter adds once, the text clears, focus stays on the line, and a live region says "Added <name>".
 */
export function ApAddLine<T>({ label, inputId, query, onQuery, entries, status, emptyText, errorText = ADD_LINE_ERROR, onRetry, onPick, announceFor, role = 'physician', onFieldOpenChange, tabClosesList = false, holdEnterWhileSearching = false }: ApAddLineProps<T>) {
  const [open, setOpen] = useState(false);
  // K8 (the 6 Oct note interaction contract) and ENC-L10: at rest the line is ONE quiet "Add …" action, never an
  // empty text box in the note. Pressing it opens the field, focused; left empty, the field closes again. While it
  // holds text it stays open. The anchor id rides whichever is on screen (the held-order banner presses it).
  const [fieldOpen, setFieldOpenState] = useState(false);
  const setFieldOpen = (next: boolean) => {
    setFieldOpenState(next);
    onFieldOpenChange?.(next);
  };
  const [active, setActive] = useState<string | undefined>();
  const [announce, setAnnounce] = useState('');
  // PROD-3147: each announcement's turn. A settled pick clears only its own text, never a later one.
  const announceTurn = useRef(0);
  const menuRef = useRef<CommandMenuHandle>(null);
  const menuId = useId();
  const typed = query.trim().length > 0;
  // D-75 (3)-(4): typing OR focusing opens the list — with no text it holds your favourites.
  const showing = typed || fieldOpen;
  const menuOpen = open && showing && entries.length > 0;
  // PROD-3082: every result is listed and the menu scrolls inside a fixed height, so the row the keyboard makes active
  // is kept in view (focus stays in the field; the DS menu only marks the row).
  useEffect(() => {
    if (active) document.getElementById(active)?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const items: CommandMenuItem[] = entries.map((e) => ({
    id: e.key,
    kind: menuKind(e.kind),
    label: e.term,
    ...(e.hint ? { hint: e.hint } : {}),
    issuableBy: [role],
    ...(e.added ? { inNote: true } : {}),
  }));

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (holdEnterWhileSearching && e.key === 'Enter' && status === 'searching') {
      e.preventDefault();
      return;
    }
    // Shift+Tab too (e.key is 'Tab' for both): the first press closes, the next one moves.
    if (tabClosesList && menuOpen && e.key === 'Tab') {
      e.preventDefault();
      setOpen(false);
      announceTurn.current += 1;
      setAnnounce(ADD_LINE_TAB_CLOSED);
      return;
    }
    if (menuOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Escape')) {
      menuRef.current?.handleKey(e);
      return;
    }
    // After Esc, ↓ reopens the menu on the same text.
    if (!menuOpen && e.key === 'ArrowDown' && typed && entries.length > 0) {
      e.preventDefault();
      setOpen(true);
    }
  }

  // Enter adds ONCE: the pick clears the text, which closes the menu, so a second Enter has nothing to
  // pick (and the host refuses a duplicate diagnosis anyway). Focus never leaves the line: the menu's
  // rows take no focus (a press on one is prevented from blurring the field), so it stays here.
  function insert(item: CommandMenuItem) {
    const entry = entries.find((e) => e.key === item.id);
    if (!entry) return;
    const running = onPick(entry.value);
    const turn = ++announceTurn.current;
    setAnnounce(announceFor ? announceFor(entry.value, entry.term) : `Added ${entry.term}`);
    // PROD-3147: "Applying the order set …" stayed after the apply finished. It clears when the work settles, on success
    // and on failure; the apply's receipt (or its failure notice) is the one announcement of the outcome.
    if (isPromiseLike(running)) {
      const clear = () => {
        if (announceTurn.current === turn) setAnnounce('');
      };
      running.then(clear, clear);
    }
    onQuery('');
    setOpen(false);
  }

  return (
    <div data-slot="ap-add-line" className="flex flex-col">
      <div className="relative flex flex-col gap-1">
        {!fieldOpen && !typed ? (
          <span className="flex">
            <AddEntryButton
              id={inputId}
              data-slot="ap-add-line-open"
              onClick={() => {
                setFieldOpen(true);
                setOpen(true);
              }}
            >
              {label}
            </AddEntryButton>
          </span>
        ) : (
        <TextField
          id={inputId}
          autoFocus
          value={query}
          placeholder={label}
          aria-label={label}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? menuId : undefined}
          aria-activedescendant={menuOpen ? active : undefined}
          autoComplete="off"
          onChange={(e) => {
            // Codex P3 (6 Oct): the next pick must CHANGE the live region, even when it says the same words.
            announceTurn.current += 1;
            setAnnounce('');
            onQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            if (!query.trim()) setFieldOpen(false);
          }}
          onKeyDown={onKeyDown}
        />
        )}
        {showing && status !== 'idle' && (
          <p data-slot="ap-add-line-status" role="status" aria-live="polite" className="m-0 flex items-center gap-2 text-size-sm text-text-secondary">
            {status === 'searching' ? ADD_LINE_SEARCHING : status === 'empty' ? emptyText : errorText}
            {status === 'error' && (
              <Button variant="tertiary" size="sm" onClick={onRetry}>
                {ADD_LINE_RETRY}
              </Button>
            )}
          </p>
        )}
        {menuOpen && (
          // Keyed by the text: the active row starts at the first row of each new answer.
          <CommandMenu
            key={query}
            ref={menuRef}
            id={menuId}
            // The rows are already this line's answer, so the menu filters nothing ("/" + no text).
            query="/"
            items={items}
            role={role}
            onInsert={insert}
            onRefused={(item, reason) => {
              announceTurn.current += 1;
              setAnnounce(`${item.label}: ${reason}`);
            }}
            onClose={() => setOpen(false)}
            onActiveDescendantChange={setActive}
          />
        )}
      </div>
      <span data-slot="ap-add-line-announce" role="status" aria-live="polite" className="sr-only">
        {announce}
      </span>
    </div>
  );
}

/** A pick that runs on (an order set's apply) hands back its promise; any other pick returns nothing. */
const isPromiseLike = (v: unknown): v is PromiseLike<unknown> =>
  typeof v === 'object' && v !== null && typeof (v as { then?: unknown }).then === 'function';

/** The set list's own key (order-set-apply-service `applyOrderSetsQueryKey`). */
const isOrderSetsKey = (key: readonly unknown[]) => key[0] === 'encounter' && key[1] === 'order-sets' && key[2] === 'active';
/** A catalogue search's key carries its text; it is ours only for the text in the line. */
const carries = (key: readonly unknown[], text: string) => key.some((part) => typeof part === 'string' && part.trim() === text && text !== '');
/** The catalogue searches behind the Orders line (`useApCategorySearch` per type, and the articles). */
const ORDER_SEARCH_KEYS = new Set([
  'lab-search',
  'lab-order-search',
  'medication-search',
  'master-medication-valuesets',
  'radiology-search',
  'procedure-search',
  'referral-additional-diagnoses',
  'master-vaccination-valuesets',
  'article-search',
]);
// ── what a pick hands back ─────────────────────────────────────────────────────────────────────────

export type AddLinePick =
  | { kind: 'item'; category: ApSearchCategory; item: ApSearchItem }
  | { kind: 'order-set'; set: OrderSetApplySummary }
  | { kind: 'bundle'; bundle: ApBundle }
  | { kind: 'article'; article: EducationArticle };

/** The order sets the line offers (present only with order sets on and a user who may order). */
export interface AddLineOrderSets {
  visitCodes: string[];
}

interface SharedSources {
  orderSets?: AddLineOrderSets;
  /** The provider's bundles, offered only when order sets are off and an import exists. */
  bundles?: ApBundle[];
  canImportBundle?: boolean;
}

/**
 * The order-set (or bundle) group.
 * - Open with no text: every set, as its own group ABOVE your favourites (D-75 amendment, PROD-3085, 7 Oct).
 * - Typed text: the sets whose NAME matches, as their own group AFTER the single-item results, never interleaved with
 *   them (D-75 amendment of 9 Oct 18:05, PROD-3132). The caller places the group; `match` is the text to match, or
 *   null while the typed text has not settled (no group until it has).
 * Each row carries its kind ('Order set' / 'Bundle'). Picking one still runs today's order-set review (or the bundle
 * import); it never adds or sends anything directly.
 */
function useSetSection(
  src: SharedSources,
  active: boolean,
  match: string | null,
): { section?: AddLineSection<AddLineEntry<AddLinePick>>; loading: boolean; error: boolean } {
  // Fetched while the line is open or holds text, so a settling keystroke never re-reads the list.
  const sets = useApplyOrderSets(!!src.orderSets && active);
  if (!active) return { loading: false, error: false };
  if (match === null) return { loading: !!src.orderSets && sets.isFetching, error: false };
  if (src.orderSets) {
    const rows = orderSetsForRail(sets.data ?? [], src.orderSets.visitCodes, match).map(({ set, matches }) =>
      row({ key: `set:${set.id}`, kind: 'Order set', term: set.name, hint: `Your order sets · ${orderSetMetaLine(set, matches)}`, value: { kind: 'order-set', set } }),
    );
    return { section: { id: 'order-sets', label: 'Your order sets', rows }, loading: sets.isFetching, error: sets.isError };
  }
  if (src.canImportBundle && src.bundles) {
    const wanted = match.trim().toLowerCase();
    const rows = src.bundles
      .filter((b) => (b.bundleName ?? '').trim())
      .filter((b) => !wanted || (b.bundleName ?? '').toLowerCase().includes(wanted))
      .map((bundle, i) =>
        row({ key: `bundle:${bundle.id ?? i}`, kind: 'Bundle', term: (bundle.bundleName ?? '').trim(), hint: 'Your bundles', value: { kind: 'bundle', bundle } }),
      );
    return { section: { id: 'bundles', label: 'Your bundles', rows }, loading: false, error: false };
  }
  return { loading: false, error: false };
}

// ── Diagnoses (D-75) ───────────────────────────────────────────────────────────────────────────────

export interface ApDiagnosisAddLineProps extends SharedSources {
  providerId?: string;
  inputId?: string;
  /** Codes already on the visit: such a row shows as added. */
  existingCodes?: readonly string[];
  /** Returns the apply's promise for an order set (the line clears its "Applying" text when it settles). */
  onPick: (pick: AddLinePick) => void | PromiseLike<unknown>;
  /** `dxOrderSplit` (Andrew, 4 Nov): the line takes \fav \sug \set (lib/slash-trigger.ts `DX_SLASH_KINDS`). Absent: as before. */
  dxSlash?: boolean;
  /** \sug reads the AI diagnosis search only where the clinic has it (the A&P's `aiDiagnosisEnabled`). */
  aiDiagnosisEnabled?: boolean;
  /** \sug with no text searches the visit's chief complaint. */
  complaint?: string;
}

/** `dxOrderSplit`: what "\" alone, an unknown word and a \sug with no AI say on the Diagnoses line. */
export const DX_SLASH_HINT = 'Type \\fav for your favourites, \\sug for AI suggestions or \\set for order sets';
export const DX_SUG_NOT_SET_UP = 'AI suggestions are not set up here';
/** `dxOrderSplit`: \sug with no text and no chief complaint has nothing to search (#921 review). */
export const DX_SUG_TYPE_A_SYMPTOM = 'Type a symptom';

/** "Add diagnosis": open with no text, your order sets (or bundles) then your favourites; typed, your favourites, the
 *  ICD-10 catalogue, then the order sets (or bundles) whose name matches. */
export function ApDiagnosisAddLine({ providerId, inputId, existingCodes = [], onPick, dxSlash = false, aiDiagnosisEnabled = false, complaint, ...src }: ApDiagnosisAddLineProps) {
  const [query, setQuery] = useState('');
  // `dxOrderSplit`: a text that starts with "\\" is a trigger (\\fav \\sug \\set); the search below then reads only what that
  // trigger asks for (the text after it). Any other text is today's search.
  const parsed = dxSlash ? parseSlashQuery(query, DX_SLASH_KINDS) : null;
  const slashKind = parsed && parsed.kind !== 'menu' && parsed.kind !== 'unknown' ? parsed.kind : undefined;
  const slashRest = parsed && 'rest' in parsed ? parsed.rest : '';
  const searchText = parsed ? (slashKind === 'favourites' ? slashRest : '') : query;
  const settled = useSettled(searchText, ADD_LINE_DEBOUNCE_MS);
  const live = searchText.trim();
  const typed = live.length > 0;
  // Rows that answer an OLDER text are dropped: the catalogue rows show only once the settled text is
  // the text in the field, and never as the previous query's placeholder.
  const current = settled.trim() === live;
  // D-75 (4): the field open with no text is BROWSING — your favourites (fetched with no query), nothing else.
  const [fieldOpen, setFieldOpen] = useState(false);
  const browsing = !typed && fieldOpen;
  const readsDx = parsed ? slashKind === 'favourites' : typed || browsing;
  const search = useApCategorySearch(readsDx ? providerId : undefined, 'diagnosis', settled, !parsed);
  // \\sug: the existing AI diagnosis search (GET /api/AISearch/Diagnosis), for the text after the trigger, else the complaint.
  const sugText = (slashRest || complaint || '').trim();
  const settledSug = useSettled(sugText, ADD_LINE_DEBOUNCE_MS);
  const ai = useAiDiagnosisSearch(settledSug, slashKind === 'suggestions' && aiDiagnosisEnabled);
  // \\set: the order sets, all of them or those whose name matches the text after the trigger.
  const settledSet = useSettled(slashKind === 'sets' ? slashRest : '', ADD_LINE_DEBOUNCE_MS);
  // PROD-3174 (review): typed, the matching sets wait until the single-item searches have answered, so a late item never
  // arrives ABOVE a set already on screen (the list would jump under the clinician's pointer).
  const itemsAnswered = !search.loading && !search.stale;
  // PROD-3423 `?x=dx-search-first` (ON by default on the dev hosts): this line is the diagnosis search only (catalog 29a); the order
  // sets are not read or listed here (they stay on "Add order"), except when asked for with the `dxOrderSplit` trigger \set.
  const [dxSearchFirstOn] = useState(() => isEncounterFlagOn('dxSearchFirst'));
  const sets = useSetSection(
    src,
    parsed ? slashKind === 'sets' : !dxSearchFirstOn && (typed || browsing),
    parsed ? (settledSet.trim() === slashRest ? slashRest : null) : browsing ? '' : current && itemsAnswered ? live : null,
  );
  const setGroup = sets.section && (parsed ? slashKind === 'sets' : !dxSearchFirstOn) ? [sets.section] : [];
  const retry = useRetryFailed(
    (key) =>
      (key[0] === 'provider-favourites' && key[1] === 'diagnosis') ||
      // `dxOrderSplit` \sug: Retry asks the AI search again (#921 review).
      key[0] === 'ai-diagnosis-search' ||
      (key[0] === 'master-diagnosis-codes' && carries(key, live)) ||
      (!!src.orderSets && isOrderSetsKey(key)),
  );
  const taken = new Set(existingCodes.map(norm));
  const favourites = current ? (search.items.favourites ?? []) : [];
  // D-75 (4): no text, no catalogue. A search hook keeps its last answer as placeholder data when the text is cleared,
  // and not every hook marks it stale, so the catalogue is read only for typed text.
  const catalogue = !parsed && typed && current && !search.stale ? withoutCodes(search.items.results, favourites.map((f) => f.code)) : [];
  const entry = (prefix: string, source: string) => (i: ApSearchItem) =>
    row({
      key: `${prefix}:${i.id}`,
      kind: 'Diagnosis',
      term: i.label,
      hint: hintOf(i.code, source),
      added: !!i.code && taken.has(norm(i.code)),
      value: { kind: 'item', category: 'diagnosis', item: i },
    });
  const sugRows = slashKind === 'suggestions' && settledSug === sugText ? (ai.data ?? []) : [];
  const slashSections: AddLineSection<AddLineEntry<AddLinePick>>[] =
    slashKind === 'favourites'
      ? [{ id: 'favourites', label: 'Your favourites', rows: favourites.map(entry('fav', 'your favourites')) }]
      : slashKind === 'sets'
        ? setGroup
        : slashKind === 'suggestions'
          ? [
              {
                id: 'suggestions',
                label: 'AI suggestions',
                rows: sugRows.map((g) =>
                  entry('sug', 'AI suggestion')({ id: g.id, label: g.display, code: g.code, item: { id: g.id, code: g.code, display: g.display, system: 'ICD-10' } }),
                ),
              },
            ]
          : [];
  const entries = flat(
    parsed ? liveSections(slashSections) : liveSections([
      ...(browsing ? setGroup : []),
      { id: 'favourites', label: 'Your favourites', rows: favourites.map(entry('fav', 'your favourites')) },
      { id: 'catalogue', label: 'ICD-10 catalogue', rows: catalogue.map(entry('icd', 'ICD-10 catalogue')) },
      // PROD-3132: typed, the matching sets come after the single items, never among them.
      ...(typed ? setGroup : []),
    ]),
  );
  // The trigger's own state and empty text (the menu and an unknown word list the triggers; \\sug without AI says so).
  const slashEmpty =
    !parsed
      ? undefined
      : parsed.kind === 'unknown'
        ? `No trigger "\\${parsed.word}". ${DX_SLASH_HINT}`
        : slashKind === 'suggestions' && !aiDiagnosisEnabled
          ? DX_SUG_NOT_SET_UP
          : slashKind === 'suggestions' && !sugText
            ? DX_SUG_TYPE_A_SYMPTOM
          : slashKind === 'favourites'
            ? NO_FAVOURITES_YET
            : slashKind === 'sets'
              ? 'No order sets match'
              : slashKind === 'suggestions'
                ? DIAGNOSES_NO_MATCH
                : DX_SLASH_HINT;
  const slashLoading =
    slashKind === 'favourites' ? search.loading : slashKind === 'sets' ? sets.loading : slashKind === 'suggestions' ? ai.isFetching || settledSug !== sugText : false;
  const failed = [
    ...(search.favouritesError ? ['your favourites'] : []),
    ...(typed && search.error ? ['the ICD-10 catalogue'] : []),
    ...(sets.error ? ['your order sets'] : []),
    // \sug: the AI request failing is named too, so the line offers Retry rather than "No match" (#921 review).
    ...(slashKind === 'suggestions' && aiDiagnosisEnabled && ai.isError ? ['AI suggestions'] : []),
  ];
  const status = parsed
    ? lineStatus({ typed: true, current: true, loading: slashLoading, error: failed.length > 0, rows: entries.length, answered: !slashLoading })
    : lineStatus({
        typed,
        browsing,
        current,
        loading: search.loading || sets.loading,
        error: failed.length > 0,
        rows: entries.length,
        answered: live.length >= MIN_CATALOGUE_CHARS && !search.stale,
      });
  return (
    <ApAddLine<AddLinePick>
      label="Add diagnosis"
      inputId={inputId}
      query={query}
      onQuery={setQuery}
      onFieldOpenChange={setFieldOpen}
      entries={entries}
      status={status}
      emptyText={slashEmpty ?? (browsing ? (dxSearchFirstOn ? DX_TYPE_TO_SEARCH : NO_FAVOURITES_YET) : DIAGNOSES_NO_MATCH)}
      errorText={failureText(failed, entries.length)}
      onRetry={retry}
      announceFor={announcePick}
      onPick={onPick}
    />
  );
}

// ── Orders (D-75 amendment) ────────────────────────────────────────────────────────────────────────

export interface ApOrderAddLineProps extends SharedSources {
  providerId?: string;
  /** The order types this user may place: permission DATA from the host, never decided here. */
  allowedOrderTypes: readonly DsOrderType[];
  /** Education articles are offered when the host can add one. */
  canAddArticle?: boolean;
  /** `<type>:<CODE>` of the orders already on the plan: such a row shows as added. */
  existingOrderKeys?: readonly string[];
  inputId?: string;
  /** Returns the apply's promise for an order set (the line clears its "Applying" text when it settles). */
  onPick: (pick: AddLinePick) => void | PromiseLike<unknown>;
  /** Controlled text in the field (the one Orders line with the backslash triggers holds it); absent, the line holds its own. */
  query?: string;
  onQuery?: (query: string) => void;
  onFieldOpenChange?: (open: boolean) => void;
  /** The text the Add order search reads, when it is not the field's text ('' while the field holds a backslash query). */
  searchQuery?: string;
  /** The field state the Add order search reads, when it is not the field's own (closed while the backslash search runs). */
  searchFieldOpen?: boolean;
  /** The line's rows, status, texts and pick handler from another search (the backslash search), drawn instead of Add order's. */
  override?: Partial<Pick<ApAddLineProps<AddLinePick>, 'entries' | 'status' | 'emptyText' | 'errorText' | 'onRetry' | 'onPick' | 'tabClosesList' | 'holdEnterWhileSearching'>>;
}

/** "Add order": open with no text, your order sets then your favourites per allowed type; typed, your favourites, then
 *  each allowed type's catalogue, then the education library, then the order sets (or bundles) whose name matches. */
export function ApOrderAddLine({ inputId, query: shownQuery, onQuery, onFieldOpenChange, searchQuery, searchFieldOpen, override, ...props }: ApOrderAddLineProps) {
  const [ownQuery, setOwnQuery] = useState('');
  // D-75 (4), the Orders amendment: the field open with no text shows your favourites of every type you may place.
  const [fieldOpen, setFieldOpen] = useState(false);
  const query = shownQuery ?? ownQuery;
  const line = useOrderSearch({ ...props, query: searchQuery ?? query, fieldOpen: searchFieldOpen ?? fieldOpen });
  return (
    <ApAddLine<AddLinePick>
      label="Add order"
      inputId={inputId}
      query={query}
      onQuery={onQuery ?? setOwnQuery}
      onFieldOpenChange={(open) => {
        setFieldOpen(open);
        onFieldOpenChange?.(open);
      }}
      {...line}
      {...override}
      announceFor={announcePick}
    />
  );
}

/** The "Add order" search for a query and field state: the rows, status and texts `ApAddLine` draws. An empty query with the field
 *  closed reads nothing. */
function useOrderSearch({
  providerId,
  allowedOrderTypes,
  canAddArticle,
  existingOrderKeys = [],
  onPick,
  query,
  fieldOpen,
  ...src
}: Omit<ApOrderAddLineProps, 'inputId' | 'query' | 'onQuery' | 'onFieldOpenChange' | 'searchQuery' | 'searchFieldOpen' | 'override'> & {
  query: string;
  fieldOpen: boolean;
}) {
  const [referralsOn] = useState(() => isEncounterFlagOn('referrals'));
  const settled = useSettled(query, ADD_LINE_DEBOUNCE_MS);
  const live = query.trim();
  const typed = live.length > 0;
  const current = settled.trim() === live;
  const allowed = new Set(allowedOrderTypes);
  const browsing = !typed && fieldOpen;
  const pid = (t: DsOrderType) => ((typed || browsing) && allowed.has(t) ? providerId : undefined);
  const q = (t: DsOrderType) => (allowed.has(t) ? settled : '');
  // One search per type, always called in the same order (Rules of Hooks); a type the user may not
  // place gets no text and no provider, so its hooks stay idle.
  const lab = useApCategorySearch(pid('lab'), 'lab', q('lab'), true, { labOrderSearch: true });
  const medication = useApCategorySearch(pid('medication'), 'medication', q('medication'), true);
  const imaging = useApCategorySearch(pid('imaging'), 'imaging', q('imaging'), true);
  const procedure = useApCategorySearch(pid('procedure'), 'procedure', q('procedure'), true);
  const referral = useApCategorySearch(pid('referral'), 'referral', q('referral'), true);
  const vaccination = useApCategorySearch(pid('vaccination'), 'vaccination', q('vaccination'), true);
  const byType = { lab, medication, imaging, procedure, referral, vaccination };
  const articleQuery = useSettled(query, ARTICLE_DEBOUNCE_MS);
  const articles = useArticleSearch(articleQuery, !!canAddArticle && articleQuery.trim().length > 0);
  // The articles settle later (600 ms) than the catalogues (250 ms): "No orders match" and the sets wait for them too.
  const articlesSettled = !canAddArticle || articleQuery.trim() === live;
  // PROD-3174 (review): typed, the matching sets wait until every single-item search (each allowed type, and the
  // articles) has answered, so a late item never arrives ABOVE a set already on screen.
  const itemsAnswered =
    ORDER_SEARCH.every((o) => !allowed.has(o.type) || (!byType[o.type].loading && !byType[o.type].stale)) &&
    articlesSettled &&
    !(canAddArticle && articles.isFetching);
  const sets = useSetSection(src, typed || browsing, browsing ? '' : current && itemsAnswered ? live : null);
  const setGroup = sets.section ? [sets.section] : [];
  const retry = useRetryFailed(
    (key) =>
      (key[0] === 'provider-favourites' && allowed.has(key[1] as DsOrderType)) ||
      (ORDER_SEARCH_KEYS.has(String(key[0])) && carries(key, live)) ||
      (!!src.orderSets && isOrderSetsKey(key)),
  );
  const taken = new Set(existingOrderKeys.map((k) => k.toUpperCase()));

  const searched = ORDER_SEARCH.filter((o) => allowed.has(o.type));
  // [PROD-3336] flag ON: the referral rows are the clinic's specialities, so they are not called a procedure catalogue.
  const catalogueOf = (o: (typeof ORDER_SEARCH)[number]) => (o.type === 'referral' && referralsOn ? SPECIALITY_SOURCE : o.catalogue);
  const sections: AddLineSection<AddLineEntry<AddLinePick>>[] = [];
  if (current) {
    for (const o of searched) {
      const found = byType[o.type];
      const favs = found.items.favourites ?? [];
      // D-75 (4): no text, no catalogue (a cleared search may still hold its last answer as placeholder data).
      const rest = !typed || found.stale ? [] : withoutCodes(found.items.results, favs.map((f) => f.code));
      const catalogue = catalogueOf(o);
      const entry = (prefix: string, source: string) => (i: ApSearchItem) =>
        row({
          key: `${prefix}:${o.type}:${i.id}`,
          kind: o.kind,
          term: i.label,
          // A speciality row hides its GUID and says when no practitioner is active (PROD-3336).
          hint: o.type === 'referral' && referralsOn && prefix === 'cat' ? specialityRowHint(i.item) : hintOf(i.code, source),
          added: !!i.code && taken.has(`${o.type}:${norm(i.code)}`.toUpperCase()),
          value: { kind: 'item', category: o.type, item: i },
        });
      sections.push({ id: `${o.type}-favourites`, label: `${o.group} · your favourites`, rows: favs.map(entry('fav', 'your favourites')) });
      sections.push({ id: `${o.type}-catalogue`, label: `${o.group} · ${catalogue}`, rows: rest.map(entry('cat', catalogue)) });
    }
  }
  const articleRows: Row[] =
    canAddArticle && articleQuery.trim() === live && live
      ? (articles.data ?? []).flatMap((g) =>
          (g.articles ?? [])
            .filter((a) => a.display)
            .map((a, i) =>
              row({
                key: `article:${a.id ?? `${a.display}-${i}`}`,
                kind: 'Education',
                term: a.display as string,
                hint: 'Education library',
                value: { kind: 'article', article: { display: a.display as string, articleUrl: a.articleUrl, summary: a.summary } },
              }),
            ),
        )
      : [];
  // Open with no text: your order sets, then your favourites of every type. Typed: favourites, each type's catalogue,
  // education, then the sets whose name matches (D-75 amendment of 9 Oct, PROD-3132: after the single items, never
  // among them).
  const ordered = [
    ...(browsing ? setGroup : []),
    ...sections.filter((s) => s.id.endsWith('-favourites')),
    ...sections.filter((s) => s.id.endsWith('-catalogue')),
    { id: 'education', label: 'Education library', rows: articleRows },
    ...(typed ? setGroup : []),
  ];
  const entries = flat(liveSections(ordered));
  // A failed source while others answered: the rows that came back stay, and the line names what failed
  // (a favourites read and the article search count, Codex P2, 6 Oct).
  const failed = [
    ...(typed ? searched.filter((o) => byType[o.type].error).map((o) => o.group) : []),
    ...searched.filter((o) => byType[o.type].favouritesError).map((o) => `${o.group} favourites`),
    // The articles are searched only for typed text, so their failure is named only then (PROD-3095).
    ...(typed && canAddArticle && articles.isError ? ['Education'] : []),
    ...(sets.error ? ['order sets'] : []),
  ];
  const errorText = failureText(failed, entries.length);
  const status = lineStatus({
    typed,
    browsing,
    current: current && articlesSettled,
    loading: searched.some((o) => byType[o.type].loading) || sets.loading || (!!canAddArticle && articles.isFetching),
    error: failed.length > 0,
    rows: entries.length,
    answered: live.length >= MIN_CATALOGUE_CHARS && searched.every((o) => !byType[o.type].stale),
  });
  return { entries, status, emptyText: browsing ? NO_FAVOURITES_YET : ORDERS_NO_MATCH, errorText, onRetry: retry, onPick };
}
